import { useState, useEffect, useCallback } from "react";
import { collection, query, orderBy, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { F, D } from "../../../styles/palette";
import { DC, DCk, Bdg } from "../../shared";
import { CONSENT_DATOS, CONSENT_FOTOGRAFIA, CONSENT_PROCEDIMIENTO } from "../../../utils/consentText";
import { computeHash } from "../../../utils/hashIntegrity";
import { logAudit } from "../../../utils/auditTrail";

const CONSENT_TYPES = [
  { key: "datos", title: "Datos Personales (Ley 1581/2012)", text: CONSENT_DATOS, accent: "#8b5cf6" },
  { key: "fotografia", title: "Registro Fotográfico", text: CONSENT_FOTOGRAFIA, accent: "#0284c7" },
  { key: "procedimiento", title: "Procedimiento", text: CONSENT_PROCEDIMIENTO, accent: "#ea580c" },
];

const PRINT_STYLES = `
@media print {
  body > * { display: none !important; }
  #consent-print-root { display: block !important; }
  #consent-print-root * { display: revert !important; }
  .no-print { display: none !important; }
  body { background: #fff !important; color: #000 !important; font-family: Georgia, serif; }
  .consent-print-block { page-break-inside: avoid; margin-bottom: 24px; border: 1px solid #ccc; padding: 16px; border-radius: 4px; }
  .consent-print-hash { font-family: monospace; font-size: 10px; color: #555; word-break: break-all; }
}
@media screen {
  #consent-print-root {}
}
`;

export default function ConsentimientoInformado({ patientId, patient, user }) {
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState({});
  const [saving, setSaving] = useState(null);
  const [printing, setPrinting] = useState(false);

  const fullName = patient
    ? `${patient.primerNombre} ${patient.segundoNombre || ""} ${patient.primerApellido} ${patient.segundoApellido || ""}`.replace(/\s+/g, " ").trim()
    : "";

  const loadConsents = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const q = query(collection(db, "patients", patientId, "consent_records"), orderBy("fecha", "desc"));
      const snap = await getDocs(q);
      const byType = {};
      snap.docs.forEach(d => {
        const data = { id: d.id, ...d.data() };
        if (!byType[data.tipo]) byType[data.tipo] = data;
      });
      setSaved(byType);
    } catch (err) {
      console.error("Error loading consents:", err);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { loadConsents(); }, [loadConsents]);

  const handleAccept = async (type) => {
    if (saved[type.key] || !checks[type.key]) return;
    setSaving(type.key);
    try {
      const now = new Date();
      const timestampISO = now.toISOString();

      // SHA-256 firma digital del consentimiento (Ley 2015/2020)
      const hashPayload = {
        tipo: type.key,
        pacienteNombre: fullName,
        pacienteDocumento: `${patient?.tipoDocumento || ""} ${patient?.numeroDocumento || ""}`,
        doctorId: user?.uid,
        timestamp: timestampISO,
        textoConsentimiento: type.text.slice(0, 500), // primeros 500 chars como referencia
      };
      const hashConsentimiento = await computeHash(hashPayload);

      const firmaDigital = {
        doctorId: user?.uid,
        doctorNombre: user?.displayName || user?.email,
        registroMedico: user?.registroMedico || null,
        timestamp: timestampISO,
        hashConsentimiento,
      };

      const docRef = await addDoc(collection(db, "patients", patientId, "consent_records"), {
        tipo: type.key,
        titulo: type.title,
        fecha: serverTimestamp(),
        pacienteNombre: fullName,
        pacienteDocumento: `${patient?.tipoDocumento || ""} ${patient?.numeroDocumento || ""}`,
        doctorId: user?.uid,
        doctorNombre: user?.displayName || user?.email,
        aceptado: true,
        firmaDigital,
      });

      await logAudit("aceptar_consentimiento", "consent_records", docRef.id, user, {
        tipo: type.key,
        patientId,
        hashConsentimiento: hashConsentimiento.slice(0, 16),
      });

      await loadConsents();
    } catch (err) {
      console.error("Error saving consent:", err);
    } finally {
      setSaving(null);
    }
  };

  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 100);
  };

  const anyAccepted = Object.keys(saved).length > 0;

  if (loading) {
    return <p style={{ color: D.txM, fontSize: 13, textAlign: "center", padding: 20 }}>Cargando consentimientos...</p>;
  }

  return (
    <>
      {/* Estilos de impresión inyectados */}
      <style>{PRINT_STYLES}</style>

      {/* Botón imprimir (solo si hay al menos un consentimiento aceptado) */}
      {anyAccepted && (
        <div className="no-print" style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button onClick={handlePrint} disabled={printing}
            style={{ padding: "7px 16px", borderRadius: 7, border: `1px solid ${D.ac}`, background: "transparent", color: D.ac, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: F.b, display: "flex", alignItems: "center", gap: 6 }}>
            🖨 {printing ? "Preparando..." : "Imprimir / Guardar PDF"}
          </button>
        </div>
      )}

      {/* Vista normal (pantalla) */}
      <div>
        {CONSENT_TYPES.map(type => {
          const existing = saved[type.key];
          const isChecked = checks[type.key] || false;
          const isSaving = saving === type.key;
          const fecha = existing?.fecha?.toDate ? existing.fecha.toDate().toLocaleString("es-CO") : "";
          const hash = existing?.firmaDigital?.hashConsentimiento;

          return (
            <DC key={type.key} title={type.title} accent={type.accent}>
              {existing ? (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Bdg t="✓ Aceptado" c="#16a34a" />
                      {hash && <Bdg t="🔒 Firmado digitalmente" c="#0369a1" />}
                    </div>
                    <span style={{ color: "#64748b", fontSize: 11, fontFamily: F.m }}>{fecha}</span>
                  </div>
                  <div style={{ padding: 10, background: "rgba(30,41,59,0.5)", borderRadius: 8, marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>Paciente: <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{existing.pacienteNombre}</span></div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>Documento: <span style={{ color: "#e2e8f0" }}>{existing.pacienteDocumento}</span></div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>Doctor: <span style={{ color: "#e2e8f0" }}>{existing.doctorNombre}</span></div>
                    {hash && (
                      <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid rgba(100,116,139,0.2)" }}>
                        <div style={{ fontSize: 10, color: "#64748b", fontFamily: F.m }}>
                          Hash SHA-256: <span style={{ color: "#38bdf8" }}>{hash.slice(0, 16)}…</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <details style={{ cursor: "pointer" }}>
                    <summary style={{ color: D.ac, fontSize: 12, fontFamily: F.b }}>Ver texto del consentimiento</summary>
                    <div style={{ maxHeight: 180, overflowY: "auto", background: "rgba(30,41,59,0.4)", padding: 12, borderRadius: 8, marginTop: 6 }}>
                      <pre style={{ color: "#94a3b8", fontSize: 11, fontFamily: F.b, whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.5 }}>{type.text}</pre>
                    </div>
                  </details>
                </div>
              ) : (
                <div>
                  <div style={{ maxHeight: 200, overflowY: "auto", background: "rgba(30,41,59,0.6)", padding: 14, borderRadius: 8, marginBottom: 12, border: "1px solid rgba(100,116,139,0.1)" }}>
                    <pre style={{ color: "#cbd5e1", fontSize: 11, fontFamily: F.b, whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.6 }}>{type.text}</pre>
                  </div>
                  <div style={{ padding: 10, background: "rgba(30,41,59,0.4)", borderRadius: 8, marginBottom: 10 }}>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>Paciente: <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{fullName}</span></div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>Documento: <span style={{ color: "#e2e8f0" }}>{patient?.tipoDocumento} {patient?.numeroDocumento}</span></div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>Doctor: <span style={{ color: "#e2e8f0" }}>{user?.displayName || user?.email}</span></div>
                    <div style={{ fontSize: 11, color: "#64748b", fontFamily: F.m, marginTop: 3 }}>{new Date().toLocaleString("es-CO")}</div>
                  </div>
                  <DCk
                    label={`He leído y acepto: ${type.title}`}
                    checked={isChecked}
                    onChange={() => setChecks(p => ({ ...p, [type.key]: !p[type.key] }))}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                    <button onClick={() => handleAccept(type)} disabled={!isChecked || isSaving}
                      style={{ padding: "8px 18px", borderRadius: 7, border: "none", background: isChecked && !isSaving ? type.accent : "#475569", color: "#fff", fontSize: 12, fontWeight: 700, cursor: isChecked && !isSaving ? "pointer" : "default", fontFamily: F.b, opacity: isChecked ? 1 : 0.5 }}>
                      {isSaving ? "Guardando..." : "Aceptar y Registrar"}
                    </button>
                  </div>
                </div>
              )}
            </DC>
          );
        })}
      </div>

      {/* Vista de impresión: solo consentimientos aceptados */}
      <div id="consent-print-root" style={{ display: "none" }}>
        <div style={{ padding: 32, fontFamily: "Georgia, serif", color: "#000", background: "#fff" }}>
          <h1 style={{ fontSize: 18, marginBottom: 4, borderBottom: "2px solid #000", paddingBottom: 8 }}>
            Consentimientos Informados — Historia Clínica
          </h1>
          <p style={{ fontSize: 12, color: "#555", marginBottom: 24 }}>
            Paciente: <strong>{fullName}</strong> &nbsp;|&nbsp;
            Documento: {patient?.tipoDocumento} {patient?.numeroDocumento}
          </p>

          {CONSENT_TYPES.map(type => {
            const existing = saved[type.key];
            if (!existing) return null;
            const fecha = existing?.fecha?.toDate ? existing.fecha.toDate().toLocaleString("es-CO") : "";
            const hash = existing?.firmaDigital?.hashConsentimiento;

            return (
              <div key={type.key} className="consent-print-block" style={{ marginBottom: 24, border: "1px solid #ccc", padding: 16, pageBreakInside: "avoid" }}>
                <h2 style={{ fontSize: 14, marginBottom: 8, color: "#000" }}>{type.title}</h2>
                <div style={{ fontSize: 11, marginBottom: 10, lineHeight: 1.5, color: "#222", whiteSpace: "pre-wrap" }}>
                  {type.text}
                </div>
                <div style={{ borderTop: "1px solid #ccc", paddingTop: 10, marginTop: 10 }}>
                  <div style={{ fontSize: 12 }}><strong>Fecha de aceptación:</strong> {fecha}</div>
                  <div style={{ fontSize: 12 }}><strong>Doctor:</strong> {existing.doctorNombre}</div>
                  {existing.firmaDigital?.registroMedico && (
                    <div style={{ fontSize: 12 }}><strong>Registro Médico:</strong> {existing.firmaDigital.registroMedico}</div>
                  )}
                  {hash && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 10, color: "#555" }}>
                        <strong>Firma digital SHA-256 (Ley 2015/2020):</strong>
                      </div>
                      <div className="consent-print-hash" style={{ fontFamily: "monospace", fontSize: 9, color: "#555", wordBreak: "break-all", marginTop: 2 }}>
                        {hash}
                      </div>
                      <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>
                        Sellado: {existing.firmaDigital?.timestamp ? new Date(existing.firmaDigital.timestamp).toLocaleString("es-CO") : ""}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <div style={{ marginTop: 32, borderTop: "1px solid #ccc", paddingTop: 12, fontSize: 10, color: "#777" }}>
            Documento generado conforme a Res. 1995/1999, Ley 1581/2012, Ley 2015/2020 y Res. 839/2017.
            Retención: 15 años.
          </div>
        </div>
      </div>
    </>
  );
}
